import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';
import { API_BASE_URL } from '../config/api';
import logger from '../utils/logger';

const AuthContext = createContext();

// Helper to detect mobile devices
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (window.innerWidth <= 768);
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null); // real bearer or '[SESSION]' sentinel
  const [sessionMode, setSessionMode] = useState(false); // true when relying on server session only
  const [serverSessionValid, setServerSessionValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load persisted bearer auth and revalidate server session on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('accessToken');
    const storedUser = localStorage.getItem('user');
    if (storedToken && storedUser) {
      try {
        setAccessToken(storedToken);
        setUser(JSON.parse(storedUser));
      } catch (e) {
        logger.error('Failed to parse stored user', e);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
      }
    }
    revalidateSession();
    
    // Handle mobile redirect callback
    handleMobileAuthCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle redirect callback from mobile auth flow
  const handleMobileAuthCallback = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const authSuccess = urlParams.get('auth_success');
    const authError = urlParams.get('auth_error');
    
    // Clean up URL params
    if (authSuccess || authError) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    if (authError) {
      setError(decodeURIComponent(authError));
      return;
    }
    
    if (authSuccess === 'true') {
      // Fetch session info from server
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/auth/me`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          if (data.account) {
            const userObj = {
              id: data.account.homeAccountId,
              displayName: data.account.name,
              userPrincipalName: data.account.username,
              mail: data.account.username
            };
            setUser(userObj);
            setAccessToken('[SESSION]');
            setSessionMode(true);
            setServerSessionValid(true);
            localStorage.setItem('user', JSON.stringify(userObj));
            logger.log('[Auth] Mobile redirect auth successful');
          }
        }
      } catch (err) {
        logger.error('[Auth] Mobile redirect auth failed:', err);
        setError('Authentication failed');
      } finally {
        setLoading(false);
      }
    }
  };

  const revalidateSession = async () => {
    try {
      const resp = await fetch(`${API_BASE_URL}/auth/status`, { credentials: 'include' });
      if (!resp.ok) { setServerSessionValid(false); return; }
      const data = await resp.json();
      setServerSessionValid(!!data.sessionAuth);
      if (data.sessionAuth && accessToken === '[SESSION]') {
        if (!user && data.sessionAccount) {
          const userObj = {
            id: data.sessionAccount.username,
            displayName: data.sessionAccount.name,
            userPrincipalName: data.sessionAccount.username,
            mail: data.sessionAccount.username
          };
          setUser(userObj);
        }
      }
    } catch {
      setServerSessionValid(false);
    }
  };

  // Token expiration auto-logout
  useEffect(() => {
    if (!accessToken) return;
    const check = () => {
      const data = parseJwt(accessToken);
      if (data?.exp && Date.now() >= data.exp * 1000) {
        logger.log('Token expired');
        logout();
      }
    };
    check();
    const interval = setInterval(check, 60000);
    return () => clearInterval(interval);
  }, [accessToken]);

  const parseJwt = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
      return JSON.parse(jsonPayload);
    } catch { return null; }
  };

  const login = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // On mobile, use redirect flow instead of popup
      if (isMobileDevice()) {
        logger.log('[Auth] Mobile device detected, using redirect flow');
        // Redirect directly to the login endpoint with redirect=true
        window.location.href = `${API_BASE_URL}/auth/login?redirect=true`;
        return; // Page will navigate away
      }
      
      // Desktop: use popup flow
      const resp = await authService.getAuthUrl();
      const authUrl = resp.authUrl;
      if (!authUrl) throw new Error('No auth URL received');
      const popup = window.open(authUrl, 'microsoftAuth', 'width=500,height=600,scrollbars=yes,resizable=yes');
      if (!popup) throw new Error('Popup blocked. Please allow popups.');
      popup.focus();

      return new Promise((resolve, reject) => {
        let authCompleted = false;
        const startTime = Date.now();
        let sessionPollInterval; // will assign later so cleanup can clear

        const cleanup = () => {
          window.removeEventListener('message', handleMessage);
          clearInterval(storageInterval);
          clearInterval(closeInterval);
          if (sessionPollInterval) clearInterval(sessionPollInterval);
        };

        const finishSuccess = (token, account, expiresOn, isSession=false) => {
          authCompleted = true;
          const totalMs = Date.now() - startTime;
          logger.log(`[Auth] Success (${isSession ? 'session' : 'token'}) in ${totalMs}ms`);
          if (isSession) {
            setAccessToken('[SESSION]');
            setSessionMode(true);
            // Do not persist session sentinel token
            localStorage.removeItem('accessToken');
          } else {
            setAccessToken(token);
            setSessionMode(false);
            localStorage.setItem('accessToken', token);
          }
          const userObj = {
            id: account?.homeAccountId,
            displayName: account?.name,
            userPrincipalName: account?.username,
            mail: account?.username
          };
          setUser(userObj);
          localStorage.setItem('user', JSON.stringify(userObj));
          setError(null);
          // Attempt to close the popup immediately after success for faster UX.
          // (Popup may also close itself after receiving ACK)
          try {
            if (popup && !popup.closed) popup.close();
          } catch {}
          cleanup();
          resolve({ accessToken: token, account, expiresOn });
        };

        const handleMessage = (event) => {
          logger.log('[Auth] Received postMessage from:', event.origin, 'data:', event.data);
          const validOrigins = [window.location.origin, 'http://localhost:5000', 'http://localhost:3000'];
          if (!validOrigins.includes(event.origin)) {
            logger.log('[Auth] Ignoring message from invalid origin:', event.origin);
            return;
          }
          if (event.data?.type === 'AUTH_SUCCESS') {
            // Send ACK immediately to close popup faster
            try {
              if (popup && !popup.closed) {
                popup.postMessage({ type: 'AUTH_ACK' }, '*');
                logger.log('[Auth] Sent AUTH_ACK to popup');
              }
            } catch (e) {
              logger.warn('[Auth] Failed to send ACK to popup', e);
            }

            if (event.data.accessToken) {
              logger.log('[Auth] PostMessage AUTH_SUCCESS with accessToken received');
              finishSuccess(event.data.accessToken, event.data.account, event.data.expiresOn, false);
            } else {
              logger.log('[Auth] PostMessage AUTH_SUCCESS (session-based) received; fetching /api/auth/me');
              // Session-based: get account info
              fetch(`${API_BASE_URL}/auth/me`, { credentials: 'include' })
                .then(r => {
                  if (!r.ok) throw new Error('Failed to fetch session account');
                  return r.json();
                })
                .then(data => {
                  // No token returned; mark authenticated using session
                  finishSuccess('[SESSION]', { homeAccountId: data.account.homeAccountId, name: data.account.name, username: data.account.username }, data.expiresOn, true);
                  revalidateSession();
                })
                .catch(err => {
                  authCompleted = true;
                  if (!popup.closed) popup.close();
                  cleanup();
                  reject(new Error('Session fetch failed: ' + err.message));
                });
            }
          } else if (event.data?.type === 'AUTH_ERROR') {
            authCompleted = true;
            if (!popup.closed) popup.close();
            cleanup();
            reject(new Error(event.data.error || 'Authentication failed'));
          }
        };
        window.addEventListener('message', handleMessage);

        const storageInterval = setInterval(() => {
          if (authCompleted) return;
          const raw = localStorage.getItem('authResult');
          if (!raw) return;
          let parsed; try { parsed = JSON.parse(raw); } catch { localStorage.removeItem('authResult'); return; }
          if ((Date.now() - (parsed.timestamp || 0)) > 60000) { localStorage.removeItem('authResult'); return; }
          if (parsed.type === 'AUTH_SUCCESS' && parsed.accessToken) {
            logger.log('[Auth] LocalStorage fallback AUTH_SUCCESS processed');
            localStorage.removeItem('authResult');
            finishSuccess(parsed.accessToken, parsed.account, parsed.expiresOn, false);
          } else if (parsed.type === 'AUTH_ERROR') {
            authCompleted = true;
            localStorage.removeItem('authResult');
            if (!popup.closed) popup.close();
            cleanup();
            reject(new Error(parsed.error || 'Authentication failed'));
          }
        }, 500);

        // Polling fallback for session establishment (handles cases where opener is null)
        sessionPollInterval = setInterval(() => {
          if (authCompleted) { clearInterval(sessionPollInterval); return; }
          // After 4s start polling /api/auth/me
          if (Date.now() - startTime < 4000) return;
          fetch(`${API_BASE_URL}/auth/me`, { credentials: 'include' })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
              if (!data || !data.account) return;
              logger.log('[Auth] Session polling detected authenticated account');
              finishSuccess('[SESSION]', { homeAccountId: data.account.homeAccountId, name: data.account.name, username: data.account.username }, data.expiresOn, true);
              revalidateSession();
              clearInterval(sessionPollInterval);
            })
            .catch(() => {});
        }, 1000);

        const closeInterval = setInterval(() => {
          if (authCompleted) return;
          let isClosed = false;
          try { isClosed = popup.closed; } catch { /* COOP/COEP may block */ }
          if (isClosed) {
            if (Date.now() - startTime < 10000) return; // grace period
            authCompleted = true;
            cleanup();
            reject(new Error('Authentication was cancelled by user'));
          }
        }, 1000);
      });
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Call backend logout to get Azure AD logout URL
      const response = await authService.logout();
      
      // Clear local state
      setUser(null);
      setAccessToken(null);
      setSessionMode(false);
      setServerSessionValid(false);
      setError(null);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      localStorage.removeItem('authResult');
      
      // Redirect to Azure AD logout to clear session cookies
      // This ensures next login will show account picker
      if (response.logoutUrl) {
        window.location.href = response.logoutUrl;
      }
    } catch (error) {
      logger.error('Logout error:', error);
      // Clear local state even if backend call fails
      setUser(null);
      setAccessToken(null);
      setSessionMode(false);
      setServerSessionValid(false);
      setError(null);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      localStorage.removeItem('authResult');
    }
  };

  const refreshToken = async () => {
    // Placeholder - implement refresh flow later
    logout();
  };

  // Re-authorize function - forces consent prompt to get new permissions
  const reauthorize = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // On mobile, use redirect flow
      if (isMobileDevice()) {
        logger.log('[Auth] Mobile device detected, using redirect flow for reauthorize');
        window.location.href = `${API_BASE_URL}/auth/reauthorize?redirect=true`;
        return;
      }
      
      // Desktop: use popup flow
      const resp = await fetch(`${API_BASE_URL}/auth/reauthorize`, { credentials: 'include' });
      const data = await resp.json();
      const authUrl = data.authUrl;
      if (!authUrl) throw new Error('No auth URL received');
      const popup = window.open(authUrl, 'microsoftAuth', 'width=500,height=600,scrollbars=yes,resizable=yes');
      if (!popup) throw new Error('Popup blocked. Please allow popups.');
      popup.focus();

      return new Promise((resolve, reject) => {
        let authCompleted = false;
        const startTime = Date.now();
        let sessionPollInterval;

        const cleanup = () => {
          window.removeEventListener('message', handleMessage);
          clearInterval(storageInterval);
          clearInterval(closeInterval);
          if (sessionPollInterval) clearInterval(sessionPollInterval);
        };

        const finishSuccess = (token, account, expiresOn, isSession=false) => {
          authCompleted = true;
          logger.log(`[Auth] Reauthorize success (${isSession ? 'session' : 'token'})`);
          if (isSession) {
            setAccessToken('[SESSION]');
            setSessionMode(true);
            localStorage.removeItem('accessToken');
          } else {
            setAccessToken(token);
            setSessionMode(false);
            localStorage.setItem('accessToken', token);
          }
          const userObj = {
            id: account?.homeAccountId,
            displayName: account?.name,
            userPrincipalName: account?.username,
            mail: account?.username
          };
          setUser(userObj);
          localStorage.setItem('user', JSON.stringify(userObj));
          setError(null);
          try {
            if (popup && !popup.closed) popup.close();
          } catch {}
          cleanup();
          resolve({ accessToken: token, account, expiresOn });
        };

        const handleMessage = (event) => {
          const validOrigins = [window.location.origin, 'http://localhost:5000', 'http://localhost:3000'];
          if (!validOrigins.includes(event.origin)) return;
          if (event.data?.type === 'AUTH_SUCCESS') {
            try {
              if (popup && !popup.closed) {
                popup.postMessage({ type: 'AUTH_ACK' }, '*');
              }
            } catch (e) {}

            if (event.data.accessToken) {
              finishSuccess(event.data.accessToken, event.data.account, event.data.expiresOn, false);
            } else {
              fetch(`${API_BASE_URL}/auth/me`, { credentials: 'include' })
                .then(r => r.ok ? r.json() : Promise.reject('Failed'))
                .then(data => {
                  finishSuccess('[SESSION]', { homeAccountId: data.account.homeAccountId, name: data.account.name, username: data.account.username }, data.expiresOn, true);
                  revalidateSession();
                })
                .catch(err => {
                  authCompleted = true;
                  cleanup();
                  reject(new Error('Session fetch failed'));
                });
            }
          } else if (event.data?.type === 'AUTH_ERROR') {
            authCompleted = true;
            cleanup();
            reject(new Error(event.data.error || 'Reauthorization failed'));
          }
        };
        window.addEventListener('message', handleMessage);

        const storageInterval = setInterval(() => {
          if (authCompleted) return;
          const raw = localStorage.getItem('authResult');
          if (!raw) return;
          let parsed; try { parsed = JSON.parse(raw); } catch { localStorage.removeItem('authResult'); return; }
          if ((Date.now() - (parsed.timestamp || 0)) > 60000) { localStorage.removeItem('authResult'); return; }
          if (parsed.type === 'AUTH_SUCCESS' && parsed.accessToken) {
            localStorage.removeItem('authResult');
            finishSuccess(parsed.accessToken, parsed.account, parsed.expiresOn, false);
          } else if (parsed.type === 'AUTH_ERROR') {
            authCompleted = true;
            localStorage.removeItem('authResult');
            cleanup();
            reject(new Error(parsed.error || 'Reauthorization failed'));
          }
        }, 500);

        sessionPollInterval = setInterval(() => {
          if (authCompleted) { clearInterval(sessionPollInterval); return; }
          if (Date.now() - startTime < 4000) return;
          fetch(`${API_BASE_URL}/auth/me`, { credentials: 'include' })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
              if (!data || !data.account) return;
              finishSuccess('[SESSION]', { homeAccountId: data.account.homeAccountId, name: data.account.name, username: data.account.username }, data.expiresOn, true);
              revalidateSession();
              clearInterval(sessionPollInterval);
            })
            .catch(() => {});
        }, 1000);

        const closeInterval = setInterval(() => {
          if (authCompleted) return;
          let isClosed = false;
          try { isClosed = popup.closed; } catch {}
          if (isClosed) {
            if (Date.now() - startTime < 10000) return;
            authCompleted = true;
            cleanup();
            reject(new Error('Reauthorization was cancelled by user'));
          }
        }, 1000);
      });
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  // Treat '[SESSION]' placeholder as authenticated (session-only server stored token)
  const isAuthenticated = () => {
    if (accessToken === '[SESSION]') {
      return sessionMode && serverSessionValid && !!user;
    }
    return !!accessToken && !!user;
  };

  const getAuthHeaders = () => (accessToken && accessToken !== '[SESSION]') ? { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' } : {};

  const value = { user, accessToken, loading, error, login, logout, refreshToken, reauthorize, isAuthenticated: isAuthenticated(), getAuthHeaders, setError, sessionMode, serverSessionValid, revalidateSession };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};